import { useEffect, useState } from "react";
import { api, asArray, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Package, Plus, X, Trash2, Boxes } from "lucide-react";

const METHODS = [
  ["winget", "winget (Windows)", "Package ID, e.g. Google.Chrome"],
  ["choco", "Chocolatey (Windows)", "Package name, e.g. 7zip"],
  ["brew", "Homebrew (macOS)", "Formula, e.g. wget"],
  ["brew_cask", "Homebrew Cask (macOS)", "Cask, e.g. google-chrome"],
  ["url", "Download URL (.msi/.exe/.pkg/.deb)", "—"],
  ["ftp", "FTP location (.msi/.exe/.pkg/.deb)", "—"],
  ["script", "Custom command", "—"],
];
const methodLabel = (m) => (METHODS.find(x => x[0] === m)?.[1] || m);
const EMPTY_ITEM = { name: "", method: "winget", package: "", args: "", url: "" };

function ItemRow({ item, onChange, onRemove }) {
  const isFtp = item.method === "ftp";
  const needsUrl = item.method === "url" || isFtp;
  const needsScript = item.method === "script";
  const needsPkg = !needsUrl && !needsScript;
  return (
    <div className="grid grid-cols-12 gap-2 items-start">
      <input value={item.name} onChange={e => onChange({ ...item, name: e.target.value })} placeholder="Display name" className="col-span-3 bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm" />
      <select value={item.method} onChange={e => onChange({ ...item, method: e.target.value })} className="col-span-3 bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm">
        {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      {needsPkg && <input value={item.package} onChange={e => onChange({ ...item, package: e.target.value })} placeholder="Package ID" className="col-span-3 bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm font-mono" />}
      {needsUrl && <input value={item.url} onChange={e => onChange({ ...item, url: e.target.value })} placeholder={isFtp ? "ftp://user:pass@host/path/installer.msi" : "https://…/installer.msi"} className="col-span-3 bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm font-mono" />}
      {needsScript && <input value={item.args} onChange={e => onChange({ ...item, args: e.target.value })} placeholder="command to run" className="col-span-3 bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm font-mono" />}
      <input value={item.args} onChange={e => onChange({ ...item, args: e.target.value })} placeholder={needsScript ? "(unused)" : "extra args (optional)"} disabled={needsScript} className="col-span-2 bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-sm font-mono disabled:opacity-40" />
      <button onClick={onRemove} className="col-span-1 text-zinc-600 hover:text-red-400 flex justify-center pt-1.5"><Trash2 size={15} /></button>
    </div>
  );
}

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // template being edited/created
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/install-templates").then(r => setTemplates(asArray(r.data))).catch(() => setTemplates([]));
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ name: "", description: "", items: [{ ...EMPTY_ITEM }] });
  const openEdit = (t) => setEditing({ ...t, items: (t.items || []).map(i => ({ ...EMPTY_ITEM, ...i })) });

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Template name is required"); return; }
    const items = (editing.items || []).filter(i => i.name.trim() || i.package.trim() || i.url.trim() || i.args.trim());
    setSaving(true);
    try {
      if (editing.id) await api.patch(`/install-templates/${editing.id}`, { name: editing.name, description: editing.description, items });
      else await api.post("/install-templates", { name: editing.name, description: editing.description, items });
      toast.success("Template saved");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/install-templates/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const setItem = (idx, next) => setEditing(e => ({ ...e, items: e.items.map((it, i) => i === idx ? next : it) }));
  const addItem = () => setEditing(e => ({ ...e, items: [...(e.items || []), { ...EMPTY_ITEM }] }));
  const removeItem = (idx) => setEditing(e => ({ ...e, items: e.items.filter((_, i) => i !== idx) }));

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Administration</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Install Templates</h1>
          <p className="text-sm text-zinc-500 mt-1">Reusable software sets. Assign a template to a company, then onboarding a new endpoint installs the whole set.</p>
        </div>
        <button onClick={openNew} data-testid="tpl-new" className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md px-3 py-1.5"><Plus size={14} /> New template</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {templates.map(t => (
          <div key={t.id} className="bg-zinc-950 border border-zinc-900 rounded-md p-4" data-testid={`tpl-card-${t.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2"><Boxes size={15} className="text-blue-400" /><h2 className="font-display text-base tracking-tight">{t.name}</h2></div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(t)} className="text-xs text-blue-400 hover:text-blue-300">Edit</button>
                <button onClick={() => remove(t)} className="text-zinc-600 hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            </div>
            {t.description && <div className="text-xs text-zinc-500 mt-1">{t.description}</div>}
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono mt-3 mb-1">{(t.items || []).length} apps</div>
            <div className="flex flex-wrap gap-1">
              {(t.items || []).slice(0, 8).map((i, idx) => <span key={idx} className="text-[10px] bg-zinc-900 border border-zinc-800 rounded px-2 py-0.5 text-zinc-400">{i.name || i.package}</span>)}
            </div>
          </div>
        ))}
        {templates.length === 0 && <div className="text-sm text-zinc-500 col-span-full py-8 text-center">No templates yet — create one to define a company's standard software set.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-6" onClick={() => setEditing(null)}>
          <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-4xl w-full my-8" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-900">
              <h2 className="font-display text-lg font-semibold">{editing.id ? "Edit" : "New"} install template</h2>
              <button onClick={() => setEditing(null)} className="p-1.5 rounded hover:bg-zinc-900 text-zinc-400"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} placeholder="Template name" data-testid="tpl-name" className="bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
                <input value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })} placeholder="Description (optional)" className="col-span-2 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-mono px-1">
                <div className="col-span-3">Name</div><div className="col-span-3">Method</div><div className="col-span-3">Package / URL / Command</div><div className="col-span-2">Args</div><div className="col-span-1"></div>
              </div>
              <div className="space-y-2" data-testid="tpl-items">
                {(editing.items || []).map((it, idx) => <ItemRow key={idx} item={it} onChange={(n) => setItem(idx, n)} onRemove={() => removeItem(idx)} />)}
              </div>
              <button onClick={addItem} data-testid="tpl-add-item" className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300"><Plus size={14} /> Add software</button>
              <p className="text-[11px] text-zinc-500 leading-relaxed">
                The agent installs each app silently and unattended. For <span className="font-mono text-zinc-400">URL</span>/<span className="font-mono text-zinc-400">FTP</span> installers, the extension drives the silent flags automatically —
                <span className="font-mono text-zinc-400"> .msi</span> → <span className="font-mono text-zinc-400">/qn /norestart</span>, <span className="font-mono text-zinc-400">.exe</span> → <span className="font-mono text-zinc-400">/S</span> (override in <em>Args</em> if the vendor uses different quiet switches), <span className="font-mono text-zinc-400">.pkg</span> → macOS installer, <span className="font-mono text-zinc-400">.deb</span> → dpkg.
                FTP supports <span className="font-mono text-zinc-400">ftp://</span> and <span className="font-mono text-zinc-400">ftps://</span> with optional <span className="font-mono text-zinc-400">user:pass@</span> credentials.
              </p>
              <div className="flex gap-2 justify-end pt-2">
                <button onClick={() => setEditing(null)} className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-sm rounded px-4 py-2">Cancel</button>
                <button onClick={save} disabled={saving} data-testid="tpl-save" className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm rounded px-4 py-2">{saving ? "Saving…" : "Save template"}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { methodLabel };
